import { Alert, Input, InputNumber, Modal, Typography } from 'antd';
import { useScreen } from 'hooks/useScreen';
import { useRef, useState } from 'react';

import { formatMNT, queryErrorMessage } from '../CashTransactions/types';
import type { InvoiceRow } from './types';
import { newIdempotencyKey } from './types';

const { Text } = Typography;

export type InvoiceAction = 'pay' | 'void' | 'refund' | 'amount';

const TITLES: Record<InvoiceAction, string> = {
  pay: 'Картын үлдэгдлээс төлөх',
  void: 'Хүчингүй болгох',
  refund: 'Төлбөр буцаах',
  amount: 'Дүн өөрчлөх',
};

// One admin action on an Invoice. Its Idempotency key lives as long as the
// modal, so pressing OK again after a network error can't charge twice.
export default function InvoiceActionModal({
  invoice,
  action,
  onClose,
  onDone,
}: {
  invoice: InvoiceRow;
  action: InvoiceAction;
  onClose: () => void;
  onDone: () => void;
}) {
  const { runQuery } = useScreen();
  const key = useRef(newIdempotencyKey());
  const [note, setNote] = useState('');
  const [amount, setAmount] = useState<number | null>(invoice.amount);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const covers = invoice.card_balance >= invoice.amount;
  const disabled =
    (action === 'pay' && !covers) ||
    (action === 'amount' && !(amount != null && amount > 0 && amount !== invoice.amount));

  const handleOk = () => {
    setLoading(true);
    setError(null);
    const base = { invoiceId: invoice.id, idempotencyKey: key.current };
    const request =
      action === 'pay'
        ? runQuery('invoice_pay', base)
        : action === 'void'
          ? runQuery('invoice_void', { ...base, note: note.trim() || undefined })
          : action === 'refund'
            ? runQuery('invoice_refund', { ...base, note: note.trim() || undefined })
            : runQuery('invoice_set_amount', { ...base, amount, note: note.trim() || undefined });
    request
      .then(onDone)
      .catch((e) => setError(queryErrorMessage(e)))
      .finally(() => setLoading(false));
  };

  return (
    <Modal
      open
      title={TITLES[action]}
      onCancel={onClose}
      onOk={handleOk}
      okText={TITLES[action]}
      okButtonProps={{ danger: action === 'void' || action === 'refund', disabled }}
      confirmLoading={loading}
      cancelText="Болих"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Text>
          {invoice.student_name} — {invoice.fee_name}: <strong>{formatMNT(invoice.amount)}</strong>
        </Text>

        {action === 'pay' && (
          <Alert
            type={covers ? 'info' : 'warning'}
            showIcon
            message={`Картын үлдэгдэл: ${formatMNT(invoice.card_balance)} → ${formatMNT(invoice.card_balance - invoice.amount)}`}
            description={covers ? undefined : 'Үлдэгдэл бүтэн дүнд хүрэхгүй тул төлөх боломжгүй.'}
          />
        )}
        {action === 'refund' && (
          <Alert
            type="info"
            showIcon
            message={`${formatMNT(invoice.amount)} картад буцаан орно. Төлсөн гүйлгээ хэвээр үлдэж, шинэ буцаалтын гүйлгээ бичигдэнэ.`}
          />
        )}
        {action === 'amount' && (
          <InputNumber<number>
            style={{ width: '100%' }}
            min={1}
            precision={0}
            value={amount}
            onChange={setAmount}
            formatter={(value) => `${value ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            parser={(value) => Number((value ?? '').replace(/,/g, ''))}
            addonAfter="₮"
          />
        )}
        {action !== 'pay' && (
          <Input.TextArea
            rows={2}
            maxLength={500}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Тэмдэглэл (шалтгаан)"
          />
        )}

        {error && <Alert type="error" showIcon message={error} />}
      </div>
    </Modal>
  );
}
